import React, { useEffect, useState } from 'react'
import {
  Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography, Box,
} from '@material-ui/core'
import GetAppRoundedIcon from '@material-ui/icons/GetAppRounded'
import AddToHomeScreenRoundedIcon from '@material-ui/icons/AddToHomeScreenRounded'

const LS_INSTALLED = 'arjan_pwa_installed'
const LS_SNOOZE = 'arjan_pwa_snooze'
const SNOOZE_DAYS = 10
const AUTO_OPEN_DELAY = 2500
const MANUAL_OPEN_DELAY = 8000

function isStandalone() {
  try {
    return (
      window.matchMedia('(display-mode: standalone)').matches ||
      window.navigator.standalone === true ||
      document.referrer.startsWith('android-app://')
    )
  } catch (e) {
    return false
  }
}

const isIos = () => /iphone|ipad|ipod/i.test(window.navigator.userAgent || '')
const ua = () => (window.navigator.userAgent || '')
const isAndroid = () => /android/i.test(ua())
const isInApp = () => /(FBAN|FBAV|Instagram|Telegram|Line\/|WhatsApp|Twitter)/i.test(ua())
const isFirefox = () => /firefox/i.test(ua())

/** گام‌های نصب به تفکیک مرورگر/دستگاه */
const installSteps = () => {
  if (isIos()) {
    return [
      'دکمهٔ «اشتراک‌گذاری» را بزنید (مربع با فلش رو به بالا، پایین صفحه).',
      'در منوی باز‌شده «Add to Home Screen / افزودن به صفحهٔ اصلی» را انتخاب کنید.',
      'روی «Add / افزودن» بزنید؛ آیکن ارجان پیمان روی صفحهٔ اصلی می‌آید.',
    ]
  }
  if (isInApp()) {
    return [
      'این صفحه را در مرورگرِ اصلی باز کنید (منوی ⋮ → «باز کردن در Chrome/Safari»).',
      'در مرورگر، منوی ⋮ را بزنید و «نصب برنامه» یا «افزودن به صفحهٔ اصلی» را انتخاب کنید.',
    ]
  }
  if (isAndroid()) {
    return [
      'منوی سه‌نقطه (⋮) بالای مرورگر را بزنید.',
      'گزینهٔ «Install app / نصب برنامه» یا «Add to Home screen / افزودن به صفحهٔ اصلی» را انتخاب کنید.',
      'تأیید کنید؛ برنامه مثل یک اپ روی گوشی نصب می‌شود.',
    ]
  }
  if (isFirefox()) {
    return [
      'فایرفاکس نصبِ کاملِ برنامه را پشتیبانی نمی‌کند.',
      'همین آدرس را در Chrome یا Edge باز کنید و از آیکن نصبِ نوار آدرس استفاده کنید.',
    ]
  }
  return [
    'در نوار آدرسِ مرورگر، آیکن «نصب» (مربع با فلش/مانیتور) را بزنید.',
    'اگر آیکن نبود: منوی ⋮ → «Install / نصب برنامه» (یا More tools → Create shortcut).',
    'تأیید کنید؛ ارجان پیمان به‌صورت برنامهٔ مستقل نصب می‌شود.',
  ]
}
const loggedIn = () => !!localStorage.getItem('access')
const getDeferred = () => window.__arjanDeferredPrompt || null
const installedFlag = () => localStorage.getItem(LS_INSTALLED) === '1'
const snoozed = () => {
  const until = parseInt(localStorage.getItem(LS_SNOOZE) || '0', 10)
  return !!until && Date.now() < until
}

/**
 * نصب/میانبر ارجان پیمان — فقط بعد از ورود.
 * رویداد نصب در index.html زودهنگام گرفته می‌شود و اینجا نصب واقعی انجام می‌شود.
 */
const InstallPrompt = () => {
  const [deferred, setDeferred] = useState(getDeferred)
  const [auth, setAuth] = useState(loggedIn)
  const [installed, setInstalled] = useState(installedFlag)
  const [open, setOpen] = useState(false)
  const [manual, setManual] = useState(false)
  const [busy, setBusy] = useState(false)
  const [chipHidden, setChipHidden] = useState(false)

  // رصد ورود کاربر (localStorage 'access')
  useEffect(() => {
    const iv = setInterval(() => setAuth(loggedIn()), 2000)
    return () => clearInterval(iv)
  }, [])

  useEffect(() => {
    const onReady = () => setDeferred(getDeferred())
    const onInstalled = () => {
      try { localStorage.setItem(LS_INSTALLED, '1') } catch (e) {}
      setInstalled(true); setDeferred(null); setOpen(false)
    }
    window.addEventListener('arjan-install-ready', onReady)
    window.addEventListener('arjan-installed', onInstalled)
    return () => {
      window.removeEventListener('arjan-install-ready', onReady)
      window.removeEventListener('arjan-installed', onInstalled)
    }
  }, [])

  // پیشنهاد خودکار — فقط پس از ورود، یک‌بار، و نه وقتی ذخیره/نصب شده
  useEffect(() => {
    if (!auth || installed || isStandalone()) return
    if (snoozed()) return
    const t1 = setTimeout(() => {
      if (getDeferred()) { setManual(false); setOpen(true) }
    }, AUTO_OPEN_DELAY)
    const t2 = setTimeout(() => {
      if (!getDeferred()) { setManual(true); setOpen(true) }
    }, MANUAL_OPEN_DELAY)
    return () => { clearTimeout(t1); clearTimeout(t2) }
  }, [auth, installed])

  const snooze = () => {
    try { localStorage.setItem(LS_SNOOZE, String(Date.now() + SNOOZE_DAYS * 86400000)) } catch (e) {}
    setOpen(false)
    setChipHidden(true)
  }

  const confirm = async () => {
    const p = getDeferred()
    if (!p) { snooze(); return }
    setBusy(true)
    try {
      p.prompt()
      const choice = await p.userChoice
      if (choice && choice.outcome === 'accepted') {
        try { localStorage.setItem(LS_INSTALLED, '1') } catch (e) {}
        setInstalled(true)
        setOpen(false)
      } else {
        setChipHidden(true)   // رد کرد ولی دکمه می‌ماند؟ خیر: این جلسه پنهان
      }
      window.__arjanDeferredPrompt = null
      setDeferred(null)
    } catch (e) {
      snooze()
    } finally {
      setBusy(false)
    }
  }

  if (!auth || installed || isStandalone()) return null

  const ios = isIos()
  const canInstall = !!deferred
  const showManual = manual && !canInstall

  const chip = !chipHidden && !open ? (
    <Button
      variant="contained"
      color="primary"
      disableElevation
      startIcon={<GetAppRoundedIcon />}
      onClick={() => setOpen(true)}
      style={{
        position: 'fixed', bottom: 76, left: 16, zIndex: 1400,
        fontWeight: 700, borderRadius: 22, boxShadow: '0 6px 18px rgba(26,35,126,.35)',
      }}
    >
      نصب برنامه
    </Button>
  ) : null

  return (
    <>
      {chip}
      <Dialog open={open} onClose={snooze} maxWidth="xs" fullWidth>
        <DialogTitle style={{ fontWeight: 800, color: '#1a237e' }}>
          {ios ? 'افزودن به صفحهٔ اصلی' : 'نصب ارجان پیمان'}
        </DialogTitle>
        <DialogContent>
          <Box display="flex" alignItems="center" style={{ marginBottom: 10, gap: 12 }}>
            <img
              src="/icon-192.png"
              alt="ارجان پیمان"
              width={64}
              height={64}
              style={{ borderRadius: 14, boxShadow: '0 4px 12px rgba(26,35,126,.25)' }}
            />
            <Typography style={{ fontSize: 13.5, lineHeight: 1.95 }}>
              {canInstall
                ? 'برنامهٔ ارجان پیمان را نصب کنم تا با یک لمس از روی دسکتاپ/صفحهٔ اصلی باز شود؟'
                : 'نصب خودکار برای این مرورگر ممکن نیست؛ این گام‌ها را انجام دهید:'}
            </Typography>
          </Box>
          {!canInstall && (
            <>
              <Box component="ol" style={{
                margin: '4px 0 0', paddingInlineStart: 20, fontSize: 13,
                lineHeight: 2.1, color: '#374151',
              }}>
                {installSteps().map((t, i) => (
                  <li key={i} style={{ marginBottom: 4 }}>{t}</li>
                ))}
              </Box>
              <Typography style={{ fontSize: 11.5, color: '#7a80a3', marginTop: 8 }}>
                آیکون ارجان پیمان، حلقهٔ طلایی برند است.
              </Typography>
            </>
          )}
        </DialogContent>
        <DialogActions style={{ padding: '10px 16px' }}>
          <Button onClick={snooze} disabled={busy}>بعداً</Button>
          {canInstall ? (
            <Button
              variant="contained" color="primary" disableElevation disabled={busy}
              startIcon={<GetAppRoundedIcon />} onClick={confirm} style={{ fontWeight: 700 }}
            >
              بله، نصب کن
            </Button>
          ) : (
            <Button
              variant="contained" color="primary" disableElevation
              startIcon={<AddToHomeScreenRoundedIcon />} onClick={snooze} style={{ fontWeight: 700 }}
            >
              فهمیدم
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  )
}

export default InstallPrompt
